/**
 * AdminVendors - Vendor & Partner management page
 * List, search, filter, and manage external parties (vendors, partners, contractors, resellers)
 */

import React, { useState, useEffect } from 'react';
import AdminShell from './AdminShell';
import { adminFetch, navigate, hasPermission } from './api';

const STATUS_COLORS = {
  active: '#00D4AA',
  pending: '#3B82F6',
  suspended: '#FFB347',
  terminated: '#FF6B6B',
};

const TYPE_COLORS = {
  vendor: { bg: 'rgba(0, 87, 255, 0.12)', text: '#0057FF' },
  partner: { bg: 'rgba(192, 132, 252, 0.12)', text: '#C084FC' },
  contractor: { bg: 'rgba(255, 179, 71, 0.12)', text: '#FFB347' },
  reseller: { bg: 'rgba(0, 212, 170, 0.12)', text: '#00D4AA' },
};

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AdminVendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    type: 'vendor',
    contact_name: '',
    contact_email: '',
    phone: '',
    abn: '',
    description: '',
  });
  const [creating, setCreating] = useState(false);

  const fetchVendors = () => {
    adminFetch('/api/admin/vendors')
      .then(r => r.json())
      .then(d => { setVendors(d.vendors || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchVendors(); }, []);

  const filtered = vendors.filter(v => {
    if (filter !== 'all' && v.type !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (v.company_name || '').toLowerCase().includes(q) ||
        (v.contact_name || '').toLowerCase().includes(q) ||
        (v.contact_email || '').toLowerCase().includes(q) ||
        (v.abn || '').toLowerCase().includes(q);
    }
    return true;
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await adminFetch('/api/admin/vendors', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowCreate(false);
        setForm({
          company_name: '',
          type: 'vendor',
          contact_name: '',
          contact_email: '',
          phone: '',
          abn: '',
          description: '',
        });
        fetchVendors();
      }
    } catch {}
    setCreating(false);
  };

  const s = {
    topBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 24,
    },
    searchInput: {
      padding: '10px 14px',
      borderRadius: 8,
      border: '1px solid #2A3444',
      backgroundColor: '#1A2332',
      color: '#FFF',
      fontSize: 14,
      width: 280,
      outline: 'none',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    filterBtn: (active) => ({
      padding: '8px 16px',
      borderRadius: 6,
      border: '1px solid #2A3444',
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      backgroundColor: active ? '#1E293B' : 'transparent',
      color: active ? '#FFF' : '#8B92A5',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      transition: 'all 0.15s ease',
    }),
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      textAlign: 'left',
      padding: '12px 16px',
      fontSize: 11,
      fontWeight: 700,
      color: '#5A6175',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      borderBottom: '1px solid #2A3444',
    },
    td: {
      padding: '14px 16px',
      fontSize: 14,
      color: '#BBBEC7',
      borderBottom: '1px solid #1A2332',
    },
    createBtn: {
      padding: '10px 20px',
      borderRadius: 8,
      border: 'none',
      background: 'linear-gradient(135deg, #0057FF, #0040CC)',
      color: '#FFF',
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 200,
    },
    modalCard: {
      backgroundColor: '#0F1729',
      borderRadius: 16,
      border: '1px solid #2A3444',
      padding: 32,
      width: 500,
      maxHeight: '90vh',
      overflowY: 'auto',
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: 8,
      border: '1px solid #2A3444',
      backgroundColor: '#1A2332',
      color: '#FFF',
      fontSize: 14,
      outline: 'none',
      boxSizing: 'border-box',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    field: {
      marginBottom: 16,
    },
    label: {
      display: 'block',
      fontSize: 12,
      fontWeight: 600,
      color: '#8B92A5',
      marginBottom: 4,
    },
  };

  return (
    <AdminShell title="Vendors & Partners">
      <div style={s.topBar}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            style={s.searchInput}
            placeholder="Search vendors..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {['all', 'vendor', 'partner', 'contractor', 'reseller'].map(f => (
            <button key={f} style={s.filterBtn(filter === f)} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        {hasPermission('vendors', 'create') && (
          <button style={s.createBtn} onClick={() => setShowCreate(true)}>
            + Add Vendor
          </button>
        )}
      </div>

      <div style={{ backgroundColor: '#1A2332', borderRadius: 12, border: '1px solid #2A3444', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5A6175' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5A6175' }}>
            {vendors.length === 0 ? 'No vendors yet. Add your first vendor or partner.' : 'No vendors match your search.'}
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Company</th>
                <th style={s.th}>Type</th>
                <th style={s.th}>Contact</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Created</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => {
                const typeStyle = TYPE_COLORS[v.type] || TYPE_COLORS.vendor;
                const statusColor = STATUS_COLORS[v.status] || '#5A6175';
                return (
                  <tr key={v.id}>
                    <td style={s.td}>
                      <div style={{ fontWeight: 600, color: '#FFF' }}>{v.company_name}</div>
                      {v.abn && <div style={{ fontSize: 11, color: '#5A6175' }}>ABN: {v.abn}</div>}
                    </td>
                    <td style={s.td}>
                      <span style={{
                        padding: '3px 10px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        backgroundColor: typeStyle.bg,
                        color: typeStyle.text,
                      }}>
                        {v.type}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={{ fontSize: 13 }}>{v.contact_name || '-'}</div>
                      <div style={{ fontSize: 11, color: '#5A6175' }}>{v.contact_email || '-'}</div>
                    </td>
                    <td style={s.td}>
                      <span style={{
                        padding: '3px 10px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        backgroundColor: `${statusColor}20`,
                        color: statusColor,
                      }}>
                        {v.status || 'active'}
                      </span>
                    </td>
                    <td style={s.td}>{formatDate(v.created_at)}</td>
                    <td style={s.td}>
                      <button
                        style={{
                          padding: '5px 12px',
                          borderRadius: 6,
                          border: '1px solid #2A3444',
                          backgroundColor: 'transparent',
                          color: '#5B9DFF',
                          fontSize: 12,
                          cursor: 'pointer',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        }}
                        onClick={() => navigate(`/admin/vendors/${v.id}`)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Vendor Modal */}
      {showCreate && (
        <div style={s.modal} onClick={() => setShowCreate(false)}>
          <div style={s.modalCard} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 600 }}>Add New Vendor</h3>
            <form onSubmit={handleCreate}>
              <div style={s.field}>
                <label style={s.label}>Company Name *</label>
                <input
                  style={s.input}
                  value={form.company_name}
                  onChange={e => setForm({ ...form, company_name: e.target.value })}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>Type *</label>
                  <select
                    style={s.input}
                    value={form.type}
                    onChange={e => setForm({ ...form, type: e.target.value })}
                  >
                    <option value="vendor">Vendor</option>
                    <option value="partner">Partner</option>
                    <option value="contractor">Contractor</option>
                    <option value="reseller">Reseller</option>
                  </select>
                </div>
              </div>

              <div style={s.field}>
                <label style={s.label}>Contact Name</label>
                <input
                  style={s.input}
                  value={form.contact_name}
                  onChange={e => setForm({ ...form, contact_name: e.target.value })}
                />
              </div>

              <div style={s.field}>
                <label style={s.label}>Contact Email</label>
                <input
                  style={s.input}
                  type="email"
                  value={form.contact_email}
                  onChange={e => setForm({ ...form, contact_email: e.target.value })}
                />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>Phone</label>
                  <input
                    style={s.input}
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
                <div style={{ ...s.field, flex: 1 }}>
                  <label style={s.label}>ABN</label>
                  <input
                    style={s.input}
                    value={form.abn}
                    onChange={e => setForm({ ...form, abn: e.target.value })}
                  />
                </div>
              </div>

              <div style={s.field}>
                <label style={s.label}>Description</label>
                <textarea
                  style={{ ...s.input, minHeight: 100, resize: 'vertical' }}
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Briefly describe this vendor..."
                />
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                <button
                  type="button"
                  style={{
                    ...s.createBtn,
                    background: 'transparent',
                    border: '1px solid #2A3444',
                    color: '#8B92A5',
                    flex: 1,
                  }}
                  onClick={() => setShowCreate(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ ...s.createBtn, flex: 1 }}
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
